import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Paperclip, Save, Edit3 } from 'lucide-react';
import { ConfirmationModal } from '../../common';
import {
  ProjectTabs,
  ProjectChatInput,
  ProjectConversationCard,
  ProjectFileCard
} from '../ui';
import { useProjectDetail } from '../hooks/useProjectDetail';
import { ProjectTab } from '../ui/ProjectTabs';
import { MAX_FILE_SIZE, MAX_TOTAL_SIZE, ALLOWED_EXTENSIONS } from '../../../config/fileUploadConstants';
import { Agent } from '../../../types/agent';

/**
 * ProjectDetailPage - Página de detalhes do projeto
 *
 * Layout padronizado:
 * - Header: Label "Projetos" + Título do projeto (igual outras páginas)
 * - Tabs: Conversas, Instruções Customizadas, Arquivos (abaixo do header)
 * - Conteúdo baseado na tab ativa
 */
export const ProjectDetailPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<ProjectTab>('conversations');
  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  const [chatToDelete, setChatToDelete] = useState<{ id: number; title: string } | null>(null);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ id: number; name: string } | null>(null);

  // Instructions editing state
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [instructionsText, setInstructionsText] = useState('');
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    project,
    projectDetail,
    isLoading,
    error,
    selectedAgent,
    setSelectedAgent,
    createChat,
    isCreatingChat,
    deleteChat,
    isDeletingChat,
    uploadFiles,
    isUploadingFiles,
    deleteFile,
    isDeletingFile,
    updateInstructions
  } = useProjectDetail({ projectId });

  // Retorno do catálogo: aplica seleção vinda do state (cobre o caso de o agente
  // não estar na primeira página carregada pelo useProjectDetail)
  useEffect(() => {
    const returned = location.state?.selectedAgent as Agent | undefined;
    if (returned) {
      setSelectedAgent(returned);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Handle chat creation
  const handleCreateChat = async (question: string) => {
    try {
      await createChat(question);
    } catch (err) {
      console.error('Error creating chat:', err);
    }
  };

  // Handle chat click
  const handleChatClick = (chatId: number) => {
    if (!project) return;
    navigate(`/project/${project.id}/chat/${chatId}`);
  };

  // Handle chat delete
  const handleDeleteChatClick = (e: React.MouseEvent, chatId: number, title: string) => {
    e.stopPropagation();
    setChatToDelete({ id: chatId, title });
    setShowDeleteChatModal(true);
  };

  const handleDeleteChatConfirm = async () => {
    if (!chatToDelete) return;
    try {
      await deleteChat(chatToDelete.id);
      setShowDeleteChatModal(false);
      setChatToDelete(null);
    } catch (err) {
      console.error('Error deleting chat:', err);
    }
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} excede o limite de 5MB`);
      } else {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (!ext || !(ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
          errors.push(`${file.name} possui extensão não permitida`);
        } else {
          validFiles.push(file);
        }
      }
    });

    if (errors.length > 0) {
      alert(errors.join('\n'));
    }

    if (validFiles.length > 0) {
      try {
        await uploadFiles(validFiles);
      } catch (err) {
        alert('Falha ao enviar arquivos. Tente novamente.');
      }
    }

    e.target.value = '';
  };

  // Handle file delete
  const handleDeleteFileClick = (e: React.MouseEvent, fileId: number, fileName: string) => {
    e.stopPropagation();
    setFileToDelete({ id: fileId, name: fileName });
    setShowDeleteFileModal(true);
  };

  const handleDeleteFileConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
      setShowDeleteFileModal(false);
      setFileToDelete(null);
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };

  // Handle instructions editing
  const handleEditInstructions = () => {
    setInstructionsText(projectDetail?.custom_instructions || '');
    setIsEditingInstructions(true);
  };

  const handleSaveInstructions = async () => {
    if (!updateInstructions) return;

    setIsSavingInstructions(true);
    try {
      await updateInstructions(instructionsText);
      setIsEditingInstructions(false);
    } catch (err) {
      console.error('Error saving instructions:', err);
      alert('Falha ao salvar instruções. Tente novamente.');
    } finally {
      setIsSavingInstructions(false);
    }
  };

  const handleCancelEditInstructions = () => {
    setIsEditingInstructions(false);
    setInstructionsText('');
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getTotalFilesSize = () => {
    return projectDetail?.documents?.reduce((sum, doc) => sum + doc.file_size, 0) || 0;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center bg-[#242424]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#333333] border-t-[#F4CC04] rounded-full animate-spin" />
          <span className="text-[#666666] text-sm">Carregando projeto...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="min-h-[calc(100vh-56px)] md:min-h-screen flex items-center justify-center bg-[#242424]">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Projeto não encontrado'}</p>
          <button
            onClick={() => navigate('/projetos')}
            className="px-4 py-2 bg-[#F4CC04] text-[#1a1a1a] rounded-lg hover:bg-[#f5d633] transition-colors"
          >
            Voltar para Projetos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-[#242424]">
      {/* Header - Padrão igual outras páginas */}
      <div className="px-8 pt-8 pb-6 border-b border-[#333333]">
        <div className="flex items-center justify-between gap-4 mb-2">
          <div>
            <p
              onClick={() => navigate('/projetos')}
              className="text-[#666666] text-sm mb-1 cursor-pointer hover:text-[#F4CC04] transition-colors"
            >
              Projetos
            </p>
            <h1 className="text-white text-2xl font-bold">{project.name}</h1>
          </div>
        </div>
      </div>

      {/* Tabs - Fora do header */}
      <ProjectTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        conversationsCount={projectDetail?.chats?.length || 0}
        filesCount={projectDetail?.documents?.length || 0}
      />

      {/* Content based on active tab */}
      <div className="flex-1 min-h-0 flex flex-col">
        {/* Conversations Tab */}
        {activeTab === 'conversations' && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Chat Input - Fixo no topo */}
            <ProjectChatInput
              selectedAgent={selectedAgent}
              onOpenAgentSelector={() => navigate('/agentes', { state: { returnTo: location.pathname } })}
              onSubmit={handleCreateChat}
              isCreating={isCreatingChat}
            />

            {/* Conversations List - Área com scroll interno */}
            <div className="flex-1 min-h-0 px-8 pb-8">
              <div className="h-full max-h-[calc(100vh-350px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#333333] scrollbar-track-transparent">
                <div className="space-y-3">
                  {projectDetail?.chats && projectDetail.chats.length > 0 ? (
                    projectDetail.chats.map(chat => (
                      <ProjectConversationCard
                        key={chat.id}
                        chat={chat}
                        onClick={() => handleChatClick(chat.id)}
                        onDelete={(e) => handleDeleteChatClick(e, chat.id, chat.chat_title || 'Nova conversa')}
                      />
                    ))
                  ) : (
                    <div className="text-center py-12 text-[#666666]">
                      <p>Nenhuma conversa neste projeto ainda.</p>
                      <p className="text-sm mt-2">Inicie uma nova conversa acima.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions Tab */}
        {activeTab === 'instructions' && (
          <div className="px-8 py-6">
            <div className="bg-[#1a1a1a] border border-[#333333] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Instruções Customizadas</h3>
                {!isEditingInstructions && (
                  <button
                    onClick={handleEditInstructions}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#F4CC04] hover:bg-[#333333] rounded-lg transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Editar</span>
                  </button>
                )}
              </div>

              {isEditingInstructions ? (
                <div className="space-y-4">
                  <textarea
                    value={instructionsText}
                    onChange={(e) => setInstructionsText(e.target.value)}
                    placeholder="Digite as instruções customizadas para este projeto..."
                    className="
                      w-full h-48 p-4 bg-[#2a2a2a] border border-[#333333] rounded-lg
                      text-white text-sm placeholder:text-[#666666]
                      focus:outline-none focus:border-[#F4CC04]
                      resize-none
                    "
                  />
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={handleCancelEditInstructions}
                      disabled={isSavingInstructions}
                      className="px-4 py-2 text-sm text-[#666666] hover:text-white transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveInstructions}
                      disabled={isSavingInstructions}
                      className="
                        flex items-center gap-2 px-4 py-2
                        bg-[#F4CC04] text-[#1a1a1a] rounded-lg
                        hover:bg-[#f5d633] transition-colors
                        disabled:opacity-50
                      "
                    >
                      {isSavingInstructions ? (
                        <>
                          <div className="w-4 h-4 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin" />
                          <span>Salvando...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Salvar</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {projectDetail?.custom_instructions ? (
                    <p className="text-[#CCCCCC] whitespace-pre-wrap">
                      {projectDetail.custom_instructions}
                    </p>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-[#666666] mb-4">
                        Nenhuma instrução customizada definida para este projeto.
                      </p>
                      <button
                        onClick={handleEditInstructions}
                        className="
                          flex items-center gap-2 px-4 py-2 mx-auto
                          bg-[#F4CC04] text-[#1a1a1a] rounded-lg
                          hover:bg-[#f5d633] transition-colors
                        "
                      >
                        <Edit3 className="w-4 h-4" />
                        <span>Adicionar instruções</span>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <div className="flex-1 min-h-0 flex flex-col px-8 py-6">
            {/* Header with upload button - Fixo no topo */}
            <div className="flex items-center justify-between mb-6 flex-shrink-0">
              <div>
                <p className="text-[#666666] text-sm">
                  {projectDetail?.documents?.length || 0} arquivo(s) •{' '}
                  {formatFileSize(getTotalFilesSize())} / {formatFileSize(MAX_TOTAL_SIZE)}
                </p>
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploadingFiles}
                className="
                  flex items-center gap-2 px-4 py-2
                  bg-[#F4CC04] text-[#1a1a1a] rounded-lg
                  hover:bg-[#f5d633] transition-colors
                  disabled:opacity-50
                "
              >
                {isUploadingFiles ? (
                  <>
                    <div className="w-4 h-4 border-2 border-[#1a1a1a] border-t-transparent rounded-full animate-spin" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <Paperclip className="w-4 h-4" />
                    <span>Adicionar arquivo</span>
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.txt,.csv,.json,.xml"
              />
            </div>

            {/* Files List - Área com scroll interno */}
            <div className="flex-1 min-h-0 max-h-[calc(100vh-320px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-[#333333] scrollbar-track-transparent">
              <div className="space-y-3">
                {projectDetail?.documents && projectDetail.documents.length > 0 ? (
                  projectDetail.documents.map(doc => (
                    <ProjectFileCard
                      key={doc.id}
                      document={doc}
                      onClick={() => {/* Preview logic */}}
                      onDelete={(e) => handleDeleteFileClick(e, doc.id, doc.original_filename)}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 text-[#666666]">
                    <Paperclip className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum arquivo anexado ao projeto.</p>
                    <p className="text-sm mt-2">Clique em "Adicionar arquivo" para começar.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Chat Modal */}
      <ConfirmationModal
        isOpen={showDeleteChatModal}
        title="Excluir Conversa"
        message={
          chatToDelete
            ? `Tem certeza que deseja excluir a conversa "${chatToDelete.title}"? Esta ação não pode ser desfeita.`
            : 'Tem certeza que deseja excluir esta conversa?'
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleDeleteChatConfirm}
        onCancel={() => {
          setShowDeleteChatModal(false);
          setChatToDelete(null);
        }}
        isLoading={isDeletingChat}
        variant="danger"
      />

      {/* Delete File Modal */}
      <ConfirmationModal
        isOpen={showDeleteFileModal}
        title="Excluir Arquivo"
        message={
          fileToDelete
            ? `Tem certeza que deseja excluir o arquivo "${fileToDelete.name}"? Esta ação não pode ser desfeita.`
            : 'Tem certeza que deseja excluir este arquivo?'
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleDeleteFileConfirm}
        onCancel={() => {
          setShowDeleteFileModal(false);
          setFileToDelete(null);
        }}
        isLoading={isDeletingFile}
        variant="danger"
      />
    </div>
  );
};
